import React, { FC } from 'react'
import { Tr } from '../../../../_metronic/i18n/Tr'

interface BannerForm {
  image: File | null
  title: string
  subtitle: string
  titleRu: string
  subtitleRu: string
  titleUz: string
  subtitleUz: string
}

interface BannerErrors {
  image: string
  title: string
  subtitle: string
  titleRu: string
  titleUz: string
}

interface AddAdvertisementBannerTabProps {
  cropDim: { w: number; h: number }
  ratioLabel: string
  bannerForm: BannerForm
  bannerErrors: BannerErrors
  bannerSourceImage: string
  bannerCropZoom: number
  bannerCropX: number
  bannerCropY: number
  bannerCropApplied: boolean
  bannerIsNewImage: boolean
  placement: string
  cropContainerRef?: React.RefObject<HTMLDivElement>
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onCropMouseDown: (e: React.MouseEvent) => void
  onCropMouseMove: (e: React.MouseEvent) => void
  onCropMouseUp: () => void
  onCropZoomChange: (value: number) => void
  onApplyCrop: () => void
  onResetCrop: () => void
  onTitleChange: (value: string) => void
  onSubtitleChange: (value: string) => void
  onTitleRuChange: (value: string) => void
  onSubtitleRuChange: (value: string) => void
  onTitleUzChange: (value: string) => void
  onSubtitleUzChange: (value: string) => void
  onPrevious: () => void
  onNext: () => void
}

const AddAdvertisementBannerTab: FC<AddAdvertisementBannerTabProps> = ({
  cropDim,
  ratioLabel,
  bannerForm,
  bannerErrors,
  bannerSourceImage,
  bannerCropZoom,
  bannerCropX,
  bannerCropY,
  bannerCropApplied,
  bannerIsNewImage,
  placement,
  cropContainerRef,
  onFileChange,
  onCropMouseDown,
  onCropMouseMove,
  onCropMouseUp,
  onCropZoomChange,
  onApplyCrop,
  onResetCrop,
  onTitleChange,
  onSubtitleChange,
  onTitleRuChange,
  onSubtitleRuChange,
  onTitleUzChange,
  onSubtitleUzChange,
  onPrevious,
  onNext,
}) => {
  const t = Tr()
  const bannerHasErrors = Object.values(bannerErrors).some((err) => err)

  return (
    <>
      {/* ── Card 1: Banner Image & Preview ───────────────────────────── */}
      <div className='card card-flush py-4 mb-6'>
        <div className='card-header d-flex align-items-center'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <rect x='3' y='3' width='18' height='18' rx='3' stroke='#009ef7' strokeWidth='1.8' />
                <path d='M3 15l5-5 4 4 3-3 6 6' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                <circle cx='8.5' cy='8.5' r='1.5' fill='#009ef7' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Banner Content')}</h2>
              <p className='text-muted fs-7 mb-0'>{t('Upload your banner image and configure the preview')}</p>
            </div>
          </div>
        </div>

        <div className='card-body pt-6'>
          <div className='row g-8'>
            {/* Left: upload + crop */}
            <div className='col-lg-6'>
              <div className='mb-6'>
                <label className='form-label fw-semibold text-gray-700 fs-6'>
                  {t('Banner Image')} <span className='text-danger'>*</span>
                </label>
                {/* Custom picker: the native file input's button/label text follows the browser language, not the app's */}
                <label
                  className={`input-group input-group-lg mb-0 cursor-pointer ${bannerErrors.image ? 'is-invalid' : ''}`}
                >
                  <span className='input-group-text'>{t('Choose File')}</span>
                  <span className={`form-control text-truncate ${bannerErrors.image ? 'is-invalid' : ''}`}>
                    {bannerForm.image?.name || t('No file chosen')}
                  </span>
                  <input
                    type='file'
                    className='d-none'
                    accept='.jpg,.jpeg,.png,.webp'
                    onChange={onFileChange}
                  />
                </label>
                <div className='form-text text-muted mt-2'>
                  JPG, PNG, WEBP &nbsp;·&nbsp; {t('max')} 1 MB &nbsp;·&nbsp; {t('ratio')} {ratioLabel} ({cropDim.w}×{cropDim.h}px)
                </div>
                {bannerErrors.image && (
                  <div className='text-danger fs-7 mt-1'>{bannerErrors.image}</div>
                )}
              </div>

              {bannerSourceImage && (
                <div className='mb-4'>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <label className='form-label fw-semibold text-gray-700 mb-0 fs-6'>
                      {t('Crop Image')}
                      <span className='text-muted fw-normal ms-2 fs-7'>({ratioLabel} · {cropDim.w}×{cropDim.h}px)</span>
                    </label>
                    {bannerCropApplied && (
                      <span className='badge badge-light-success fs-8 fw-bold'>✓ {t('Crop applied')}</span>
                    )}
                  </div>

                  <div
                    ref={cropContainerRef}
                    className='rounded-2 overflow-hidden'
                    style={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: `${(cropDim.h / cropDim.w) * 100}%`,
                      border: bannerCropApplied ? '2px solid #17c653' : '2px solid #009ef7',
                      cursor: 'grab',
                      userSelect: 'none',
                      background: '#1a1a2e',
                    }}
                    onMouseDown={onCropMouseDown}
                    onMouseMove={onCropMouseMove}
                    onMouseUp={onCropMouseUp}
                    onMouseLeave={onCropMouseUp}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <img
                        src={bannerSourceImage}
                        alt='crop'
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          transform: `translate(${bannerCropX}px, ${bannerCropY}px) scale(${bannerCropZoom})`,
                          transformOrigin: 'center center',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>
                  </div>

                  <div className='mt-4'>
                    <div className='d-flex justify-content-between mb-2'>
                      <small className='text-muted fw-semibold'>{t('Zoom')}</small>
                      <small className='text-muted'>{bannerCropZoom.toFixed(2)}×</small>
                    </div>
                    <input
                      type='range'
                      className='form-range'
                      min='1'
                      max='3'
                      step='0.05'
                      value={bannerCropZoom}
                      onChange={(e) => onCropZoomChange(parseFloat(e.target.value))}
                    />
                  </div>

                  <div className='d-flex gap-3 mt-3'>
                    <button type='button' className='btn btn-primary btn-sm px-5' onClick={onApplyCrop} disabled={!bannerIsNewImage || bannerCropApplied}>
                      {t('Apply Crop')}
                    </button>
                    <button type='button' className='btn btn-light btn-sm px-5' onClick={onResetCrop}>
                      {t('Reset')}
                    </button>
                  </div>

                </div>
              )}

              {placement && (
                <div className='mt-4 p-4 rounded-2' style={{ background: '#f8f9fa', border: '1.5px solid #e4e6ef' }}>
                  <small className='text-muted fw-semibold text-uppercase fs-8 ls-1'>{t('Selected Placement')}</small>
                  <div className='fw-bold text-gray-800 fs-6 mt-1'>{placement}</div>
                </div>
              )}
            </div>

            {/* Right: preview */}
            <div className='col-lg-6'>
              <label className='form-label fw-semibold text-gray-700 fs-6 mb-3'>
                {t('Banner Preview')}
                <span className='text-muted fw-normal ms-2 fs-7'>({ratioLabel} · {cropDim.w}×{cropDim.h}px)</span>
              </label>

              <div className='rounded-2 overflow-hidden' style={{ border: '1.5px solid #e4e6ef' }}>
                <div
                  className='d-flex align-items-center gap-2 px-4 py-3'
                  style={{ background: '#f8f9fa', borderBottom: '1px solid #e4e6ef' }}
                >
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none'>
                    <rect x='2' y='4' width='20' height='14' rx='2' stroke='#6c757d' strokeWidth='1.8' />
                    <path d='M8 22h8M12 18v4' stroke='#6c757d' strokeWidth='1.8' strokeLinecap='round' />
                  </svg>
                  <span className='fw-semibold text-gray-600 fs-7'>{t('Live Preview')} — {cropDim.w}×{cropDim.h}px</span>
                </div>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: `${(cropDim.h / cropDim.w) * 100}%`,
                    overflow: 'hidden',
                    background: '#f3f4f6',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0 }}>
                    {bannerSourceImage ? (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={bannerSourceImage}
                            alt='preview'
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                              transform: `translate(${bannerCropX}px, ${bannerCropY}px) scale(${bannerCropZoom})`,
                              transformOrigin: 'center center',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(90deg,rgba(0,0,0,0.55) 0%,rgba(0,0,0,0.05) 65%,transparent 100%)',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '14px 16px',
                            color: '#fff',
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.3 }}>
                            {bannerForm.title || t('Your banner title')}
                          </div>
                          {bannerForm.subtitle && (
                            <div
                              title={bannerForm.subtitle}
                              style={{
                                marginTop: '4px',
                                fontSize: '13px',
                                opacity: 0.88,
                                maxWidth: '70%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {bannerForm.subtitle}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                          <rect x='3' y='3' width='18' height='18' rx='3' stroke='#adb5bd' strokeWidth='1.5' />
                          <path d='M3 15l5-5 4 4 3-3 6 6' stroke='#adb5bd' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                          <circle cx='8.5' cy='8.5' r='1.5' fill='#adb5bd' />
                        </svg>
                        <span className='text-muted fs-7'>{t('Upload an image to see preview')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>

      {/* ── Card 2: Banner Titles (all 3 languages) ───────────────────── */}
      <div className='card card-flush py-4 mb-6'>
        <div className='card-header'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                <path d='M4 6h16M4 12h10M4 18h7' stroke='#009ef7' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Banner Title')}</h2>
              <p className='text-muted fs-7 mb-0'>{t('Enter the banner title in all three languages')}</p>
            </div>
          </div>
        </div>

        <div className='card-body pt-6'>
          <div className='row g-6'>
            {/* English Title */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇬🇧</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('English')}</div>
                    <div className='text-muted fs-8'>{t('Default language')}</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700 required'>
                  {t('Title')}
                </label>
                <input
                  type='text'
                  className={`form-control ${bannerErrors.title ? 'is-invalid' : ''}`}
                  value={bannerForm.title || ''}
                  onChange={(e) => onTitleChange(e.target.value)}
                  maxLength={40}
                  placeholder={t('Enter title in English')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 40 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.title?.length || 0) >= 40 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.title?.length || 0}/40
                  </small>
                </div>
                {bannerErrors.title && (
                  <div className='text-danger fs-7 mt-1'>{bannerErrors.title}</div>
                )}
              </div>
            </div>

            {/* Russian Title */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇷🇺</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('Russian')}</div>
                    <div className='text-muted fs-8'>Русский</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700 required'>
                  {t('Title')}
                </label>
                <input
                  type='text'
                  className={`form-control ${bannerErrors.titleRu ? 'is-invalid' : ''}`}
                  value={bannerForm.titleRu || ''}
                  onChange={(e) => onTitleRuChange(e.target.value)}
                  maxLength={40}
                  placeholder={t('Enter title in Russian')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 40 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.titleRu?.length || 0) >= 40 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.titleRu?.length || 0}/40
                  </small>
                </div>
                {bannerErrors.titleRu && (
                  <div className='text-danger fs-7 mt-1'>{bannerErrors.titleRu}</div>
                )}
              </div>
            </div>

            {/* Uzbek Title */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇺🇿</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('Uzbek')}</div>
                    <div className='text-muted fs-8'>O'zbekcha</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700 required'>
                  {t('Title')}
                </label>
                <input
                  type='text'
                  className={`form-control ${bannerErrors.titleUz ? 'is-invalid' : ''}`}
                  value={bannerForm.titleUz || ''}
                  onChange={(e) => onTitleUzChange(e.target.value)}
                  maxLength={40}
                  placeholder={t('Enter title in Uzbek')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 40 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.titleUz?.length || 0) >= 40 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.titleUz?.length || 0}/40
                  </small>
                </div>
                {bannerErrors.titleUz && (
                  <div className='text-danger fs-7 mt-1'>{bannerErrors.titleUz}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Card 3: Banner Subtitles (all 3 languages) ────────────────── */}
      <div className='card card-flush py-4 mb-6'>
        <div className='card-header'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                <path d='M4 6h16M4 12h14M4 18h9' stroke='#009ef7' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Banner Subtitle')}</h2>
              <p className='text-muted fs-7 mb-0'>
                {t('Enter the banner subtitle in all three languages')}
                <span className='ms-2 badge badge-light-warning fs-8'>{t('Optional')}</span>
              </p>
            </div>
          </div>
        </div>

        <div className='card-body pt-6'>
          <div className='row g-6'>
            {/* English Subtitle */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇬🇧</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('English')}</div>
                    <div className='text-muted fs-8'>{t('Default language')}</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700'>
                  {t('Subtitle')}
                  <span className='text-muted fw-normal ms-1 fs-8'>({t('Optional')})</span>
                </label>
                <input
                  type='text'
                  className='form-control'
                  value={bannerForm.subtitle || ''}
                  onChange={(e) => onSubtitleChange(e.target.value)}
                  maxLength={60}
                  placeholder={t('Enter subtitle in English')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 60 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.subtitle?.length || 0) >= 60 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.subtitle?.length || 0}/60
                  </small>
                </div>
              </div>
            </div>

            {/* Russian Subtitle */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇷🇺</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('Russian')}</div>
                    <div className='text-muted fs-8'>Русский</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700'>
                  {t('Subtitle')}
                  <span className='text-muted fw-normal ms-1 fs-8'>({t('Optional')})</span>
                </label>
                <input
                  type='text'
                  className='form-control'
                  value={bannerForm.subtitleRu || ''}
                  onChange={(e) => onSubtitleRuChange(e.target.value)}
                  maxLength={60}
                  placeholder={t('Enter subtitle in Russian')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 60 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.subtitleRu?.length || 0) >= 60 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.subtitleRu?.length || 0}/60
                  </small>
                </div>
              </div>
            </div>

            {/* Uzbek Subtitle */}
            <div className='col-lg-4'>
              <div
                className='p-5 rounded-2 h-100'
                style={{ border: '1.5px solid #e4e6ef', background: '#fafbfc' }}
              >
                <div className='d-flex align-items-center gap-2 mb-4'>
                  <span className='fs-4'>🇺🇿</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{t('Uzbek')}</div>
                    <div className='text-muted fs-8'>O'zbekcha</div>
                  </div>
                </div>
                <label className='form-label fw-semibold text-gray-700'>
                  {t('Subtitle')}
                  <span className='text-muted fw-normal ms-1 fs-8'>({t('Optional')})</span>
                </label>
                <input
                  type='text'
                  className='form-control'
                  value={bannerForm.subtitleUz || ''}
                  onChange={(e) => onSubtitleUzChange(e.target.value)}
                  maxLength={60}
                  placeholder={t('Enter subtitle in Uzbek')}
                />
                <div className='d-flex justify-content-between mt-2'>
                  <small className='text-muted'>{t('Max 60 characters')}</small>
                  <small className={`fw-semibold ${(bannerForm.subtitleUz?.length || 0) >= 60 ? 'text-danger' : 'text-muted'}`}>
                    {bannerForm.subtitleUz?.length || 0}/60
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <div className='d-flex justify-content-end mt-4 gap-3'>
        <button type='button' className='btn btn-secondary btn-lg px-8' onClick={onPrevious}>
          {t('Previous')}
        </button>
        <button type='button' className='btn btn-primary btn-lg px-8' onClick={onNext}>
          {t('Next')}
        </button>
      </div>
    </>
  )
}

export default AddAdvertisementBannerTab
